import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Check, Trash2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const token = localStorage.getItem('token');
      const [pendingRes, approvedRes] = await Promise.all([
        axios.get(`${API}/testimonials/pending`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/testimonials?approved_only=true`)
      ]);
      setTestimonials([...pendingRes.data, ...approvedRes.data]);
    } catch (error) {
      toast.error('Failed to fetch testimonials');
    } finally {
      setLoading(false);
    }
  };

  const approveTestimonial = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/testimonials/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Testimonial approved');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to approve testimonial');
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/testimonials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Testimonial deleted');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  return (
    <AdminLayout title="Testimonials">
      <div className="space-y-6">
        {/* Pending Testimonials */}
        <div>
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Pending Approval</h2>
          <div className="grid gap-4">
            {testimonials.filter(t => !t.approved).length === 0 ? (
              <div className="bg-white rounded-xl p-6 text-center text-slate-500 border border-slate-100">
                No pending testimonials
              </div>
            ) : (
              testimonials.filter(t => !t.approved).map((testimonial) => (
                <div 
                  key={testimonial.id} 
                  className="bg-white rounded-xl p-6 shadow-sm border border-slate-100"
                  data-testid={`pending-testimonial-${testimonial.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-slate-600 italic mb-4">"{testimonial.message}"</p>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="font-medium text-slate-700">{testimonial.patient_name}</span>
                        <span>{testimonial.treatment_type}</span>
                        <span>{testimonial.year}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        onClick={() => approveTestimonial(testimonial.id)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`approve-testimonial-${testimonial.id}`}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteTestimonial(testimonial.id)}
                        data-testid={`delete-testimonial-${testimonial.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Approved Testimonials */}
        <div>
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Approved Testimonials</h2>
          <div className="grid gap-4">
            {testimonials.filter(t => t.approved).length === 0 ? (
              <div className="bg-white rounded-xl p-6 text-center text-slate-500 border border-slate-100">
                No approved testimonials
              </div>
            ) : (
              testimonials.filter(t => t.approved).map((testimonial) => (
                <div 
                  key={testimonial.id} 
                  className="bg-white rounded-xl p-6 shadow-sm border border-slate-100"
                  data-testid={`approved-testimonial-${testimonial.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-slate-600 italic mb-4">"{testimonial.message}"</p>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="font-medium text-slate-700">{testimonial.patient_name}</span>
                        <span>{testimonial.treatment_type}</span>
                        <span>{testimonial.year}</span>
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">Approved</span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => deleteTestimonial(testimonial.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
